import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Briefcase, Sparkles, Search, FileText, ArrowUpRight, ShieldCheck } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/Breadcrumb";
import PageHeader from "@/components/PageHeader";
import { services } from "@/data/services";
import { Button } from "@/components/kit";

const SITE_BASE = "https://uppit.nl";

const sections = [
  {
    title: "Hoofdpagina's",
    icon: Home,
    description: "De kernpagina's van uppit.nl",
    links: [
      { label: "Home", to: "/", desc: "AI marketingbureau voor MKB & ZZP" },
      { label: "Over ons", to: "/over-ons", desc: "Het verhaal achter UPPIT en Tim Roosendaal" },
      { label: "Cases", to: "/cases", desc: "Klantsuccessen, cijfers en resultaten" },
      { label: "Contact", to: "/contact", desc: "WhatsApp, e-mail en contactformulier" },
    ],
  },
  {
    title: "Diensten",
    icon: Briefcase,
    description: "Onze 7 groeimotoren",
    links: [
      { label: "Alle diensten", to: "/diensten", desc: "Overzicht van alle 7 diensten" },
      ...services.map((s) => ({ label: s.title, to: `/diensten/${s.slug}`, desc: s.short })),
    ],
  },
  {
    title: "Tools",
    icon: Sparkles,
    description: "Gratis groeitools",
    links: [
      { label: "AI Growth Scan", to: "/ai-scan", desc: "Gratis groeiadvies in 2 minuten — powered by AI" },
    ],
  },
  {
    title: "Kennisbank",
    icon: FileText,
    description: "Praktische artikelen over online groei",
    links: [
      { label: "Blog overzicht", to: "/blog", desc: "Alle artikelen over SEO, AI-marketing, advertising en webdesign" },
      { label: "SEO uitbesteden: kosten & opbrengst", to: "/blog/seo-uitbesteden-kosten", desc: "Eerlijke prijzen en de rekensom achter SEO" },
      { label: "Zichtbaar in ChatGPT (GEO)", to: "/blog/zichtbaar-in-chatgpt-geo", desc: "Zo word je genoemd in AI-antwoorden" },
      { label: "Meta Ads: 7 dure fouten", to: "/blog/meta-ads-fouten-mkb", desc: "De meest gemaakte advertentiefouten van MKB'ers" },
      { label: "Laadtijd & conversie", to: "/blog/website-laadtijd-conversie", desc: "Waarom een trage website omzet kost" },
      { label: "AI in marketing: 5 toepassingen", to: "/blog/ai-marketing-toepassingen", desc: "AI-toepassingen met direct rendement" },
      { label: "Lokale SEO gids", to: "/blog/lokale-seo-gids", desc: "Gevonden worden door klanten in jouw regio" },
    ],
  },
  {
    title: "Juridisch",
    icon: ShieldCheck,
    description: "Privacy en cookies — AVG/GDPR",
    links: [
      { label: "Privacybeleid", to: "/privacy", desc: "Hoe UPPIT omgaat met jouw persoonsgegevens (AVG)" },
      { label: "Cookiebeleid", to: "/cookies", desc: "Welke cookies we gebruiken en hoe je je voorkeuren beheert" },
    ],
  },
  {
    title: "Voor zoekmachines & AI",
    icon: Search,
    description: "Crawlerbestanden voor optimale indexering",
    links: [
      { label: "sitemap.xml", href: `${SITE_BASE}/sitemap.xml`, desc: "XML sitemap voor Google, Bing en andere zoekmachines" },
      { label: "robots.txt", href: `${SITE_BASE}/robots.txt`, desc: "Crawlerregels (inclusief toestemming voor AI bots)" },
      { label: "llms.txt", href: `${SITE_BASE}/llms.txt`, desc: "Guide voor AI/LLM systemen (ChatGPT, Perplexity, Gemini)" },
    ],
  },
];

const SitemapPage = () => {
  useSeo({
    title: "Sitemap | Alle pagina's van UPPIT",
    description:
      "Volledige sitemap van uppit.nl: alle pagina's, diensten, tools en crawlerbestanden voor zoekmachines en AI in één overzicht.",
    path: "/sitemap",
    ogSlug: "page/diensten",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Sitemap UPPIT",
      url: `${SITE_BASE}/sitemap`,
    },
  });

  return (
    <main data-testid="sitemap-page" className="bg-ivory min-h-screen pt-28 lg:pt-32">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Sitemap", href: "/sitemap" }]} />
        <PageHeader
          kicker="Sitemap · Overzicht"
          italic="Alle pagina's,"
          bold="in één overzicht."
          lede="Een complete kaart van uppit.nl voor bezoekers, zoekmachines en AI-systemen. Alles gestructureerd voor maximale vindbaarheid."
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                data-testid={`sitemap-section-${idx}`}
                className="rounded-[1.5rem] border border-ivory-400 bg-white p-7 shadow-card-light"
              >
                <div className="flex items-start gap-4 mb-6 pb-6 border-b border-ivory-300">
                  <div className="w-12 h-12 rounded-2xl bg-electric/10 border border-electric/15 grid place-items-center text-electric shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-xl lg:text-2xl font-bold text-coal">{section.title}</h2>
                    <p className="text-sm text-coal/55 mt-1">{section.description}</p>
                  </div>
                </div>
                <ul className="space-y-1">
                  {section.links.map((link) => {
                    const isExternal = !!link.href;
                    const Wrapper = isExternal ? "a" : Link;
                    const wrapperProps = isExternal
                      ? { href: link.href, target: "_blank", rel: "noopener noreferrer" }
                      : { to: link.to };
                    return (
                      <li key={link.label}>
                        <Wrapper
                          {...wrapperProps}
                          data-testid={`sitemap-link-${link.label.toLowerCase().replace(/[\s.]+/g, "-")}`}
                          className="group flex items-start gap-3 p-3 rounded-xl hover:bg-ivory-100 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-coal font-semibold text-sm group-hover:text-electric transition-colors">{link.label}</span>
                              {isExternal && <FileText className="w-3 h-3 text-coal/30" />}
                            </div>
                            <p className="text-xs text-coal/50 mt-0.5 leading-snug">{link.desc}</p>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-coal/30 group-hover:text-electric group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-0.5" />
                        </Wrapper>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 rounded-[1.5rem] bg-obsidian text-ivory p-8 lg:p-10 relative overflow-hidden">
          <div className="glow-orb w-[360px] h-[360px] bg-electric/25 -top-20 right-10" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-ivory text-balance">
                Iets niet kunnen vinden? Stuur Tim een bericht.
              </h2>
              <p className="mt-2 text-white/60">We helpen je graag verder met je vraag.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button to="/contact" variant="primary" withArrow testid="sitemap-contact-cta">Naar contact</Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SitemapPage;
